import { useMemo, useState } from "react";

import Box from "ds/components/Box";
import Toggle from "ds/components/Toggle";
import Typography from "ds/components/Typography";
import { ScanListSchema } from "types/generated";

import ResourcesSearchInput from "../ResourcesSearchInput";

type ResourceTypesFieldProps = {
  value: string[];
  onChange: (value: string[]) => void;
  options: Array<Pick<ScanListSchema, "type">>;
  isLoading?: boolean;
  error?: string;
  helperText?: string;
};

const ResourceTypesField = ({
  value,
  onChange,
  options,
  isLoading,
  error,
  helperText,
}: ResourceTypesFieldProps) => {
  const [search, setSearch] = useState("");

  const resourceTypeOptions = useMemo(
    () => options.map(({ type }) => ({ value: type, label: type })),
    [options]
  );

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return resourceTypeOptions;
    }

    return resourceTypeOptions.filter((option) => option.value.toLowerCase().includes(query));
  }, [search, resourceTypeOptions]);

  const allSelected = value.length === resourceTypeOptions.length;
  const someSelected = value.length > 0 && !allSelected;

  const toggleAll = () => {
    onChange(allSelected ? [] : resourceTypeOptions.map((option) => option.value));
  };

  const toggleType = (typeValue: string) => {
    onChange(
      value.includes(typeValue) ? value.filter((item) => item !== typeValue) : [...value, typeValue]
    );
  };

  return (
    <Box direction="column" gap="large" grow="1" zeroMinHeight>
      <ResourcesSearchInput
        value={search}
        onChange={setSearch}
        placeholder="Search resources"
        fullWidth
      />

      <Box justify="between" align="center" fullWidth>
        <Toggle
          variant="checkbox"
          typographyVariant="p-body3"
          isSelected={allSelected}
          isIndeterminate={someSelected}
          isDisabled={isLoading}
          onChange={toggleAll}
        >
          All resources
        </Toggle>
        <Typography tag="span" variant="p-body3" color="secondary">
          {value.length} of {resourceTypeOptions.length} selected
        </Typography>
      </Box>

      <Box direction="column" gap="large" grow="1" zeroMinHeight scrollable>
        {filteredOptions.map((option) => (
          <Box key={option.value} direction="row" align="center" justify="between" fullWidth>
            <Toggle
              variant="checkbox"
              typographyVariant="p-body3"
              isSelected={value.includes(option.value)}
              isDisabled={isLoading}
              onChange={() => toggleType(option.value)}
            >
              {option.label}
            </Toggle>
          </Box>
        ))}
      </Box>

      {(error || helperText) && (
        <Typography tag="span" variant="p-body3" color="danger">
          {error ?? helperText}
        </Typography>
      )}
    </Box>
  );
};

export default ResourceTypesField;
